import { OrderFormsUpdateItem, OrderFormsUpdateResponseForm } from '@b2b/common/services/api/ordersApi/types';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { EventTicketDetailsVo, EventVo, FormVo } from '@b2b/types/event';
import { OrderEditModel, OrderEventItems } from '@b2b/types/order';
import { AdmissionType } from '@b2b/types/order/api/enums';
import { WillCallOrderItemsByEventVo } from '@b2b/types/order/api/will-call-order-detail.type';
import { v4 as uuidv4 } from 'uuid';
import { OrderFormWithResponse } from '../../../cart/components/cart-forms/types';

/**
 * Extended OrderEventItems with additional UI state properties
 * added during order processing for display purposes.
 */
export interface ProcessedOrderEvent extends OrderEventItems {
  startDate: string;
  startHour: string;
  isCancelled: boolean;
  isSuspended: boolean;
  formattedCancellationDate: string;
}

export interface EmailLanguage {
  code: string;
  name: string;
}

export interface FormatEventDatesOptions {
  dateFormat: string;
  hourFormat: string;
  parseDate: (date: string) => Date;
  toFormat: (date: Date, format: string) => string;
}

export interface ProcessEventStateOptions {
  dateTimeFormat: string;
  toFormat: (date: string | number | Date, format: string) => string;
}

/**
 * Format event dates for display.
 * Returns formatted startDate and startHour strings.
 *
 * @param eventDate - The event date string to format
 * @param options - Formatting options including date/hour formats and parsing functions
 * @returns Object containing formatted startDate and startHour
 *
 * @example
 * ```typescript
 * const result = formatEventDates('2024-03-15T10:30:00', {
 *   dateFormat: 'MMM dd, yyyy',
 *   hourFormat: 'h:mm a',
 *   parseDate: CalendarService.parseDate.bind(CalendarService),
 *   toFormat: CalendarService.toFormat.bind(CalendarService),
 * });
 * // { startDate: 'Mar 15, 2024', startHour: '10:30 AM' }
 * ```
 */
export function formatEventDates(
  eventDate: string | undefined,
  options: FormatEventDatesOptions,
): { startDate: string; startHour: string } {
  if (eventDate) {
    const parsedDate = options.parseDate(eventDate);
    return {
      startDate: options.toFormat(parsedDate, options.dateFormat),
      startHour: options.toFormat(parsedDate, options.hourFormat),
    };
  }

  return {
    startDate: 'Date N/A',
    startHour: 'Time N/A',
  };
}

/**
 * Process event state flags (cancelled, suspended).
 * Returns state flags and formatted cancellation date.
 *
 * @param state - The event state ('cancelled', 'suspended', etc.)
 * @param cancellationDate - The cancellation date if the event is cancelled
 * @param options - Formatting options including date format and formatting function
 * @returns Object containing isCancelled, isSuspended, and formattedCancellationDate
 *
 * @example
 * ```typescript
 * const result = processEventState('cancelled', '2024-03-15', {
 *   dateTimeFormat: 'MMM dd, yyyy HH:mm',
 *   toFormat: CalendarService.toFormat.bind(CalendarService),
 * });
 * // { isCancelled: true, isSuspended: false, formattedCancellationDate: 'Mar 15, 2024 00:00' }
 * ```
 */
export function processEventState(
  state: string | undefined,
  cancellationDate: string | number | Date | undefined,
  options: ProcessEventStateOptions,
): { isCancelled: boolean; isSuspended: boolean; formattedCancellationDate: string } {
  const isCancelled = state === 'cancelled';
  const isSuspended = state === 'suspended';
  const formattedCancellationDate =
    isCancelled && cancellationDate ? options.toFormat(cancellationDate, options.dateTimeFormat) : '';

  return {
    isCancelled,
    isSuspended,
    formattedCancellationDate,
  };
}

/**
 * Get the default email language from the available languages.
 * Falls back to the first language if the default code is not found.
 *
 * @param languages - Array of available email languages
 * @param defaultCode - The default language code to search for
 * @returns The matching language or the first language as fallback
 *
 * @example
 * ```typescript
 * const languages = [{ code: 'es', name: 'Spanish' }, { code: 'en-us', name: 'English' }];
 * getDefaultEmailLanguage(languages, 'en-us'); // { code: 'en-us', name: 'English' }
 * getDefaultEmailLanguage(languages, 'fr'); // { code: 'es', name: 'Spanish' } (fallback)
 * ```
 */
export function getDefaultEmailLanguage<T extends EmailLanguage>(languages: T[], defaultCode: string): T {
  return languages.find((lang) => lang.code === defaultCode) ?? languages[0];
}

/**
 * Transforms EventTicketDetailsVo from event-vo to will-call-order-detail format
 *
 * @param ticketDetails - The ticket details from EventVo
 * @returns Transformed EventTicketDetailsVo or undefined if no input
 */
export function transformTicketDetails(ticketDetails?: EventVo['ticketDetails']): EventTicketDetailsVo | undefined {
  if (!ticketDetails) {
    return undefined;
  }

  return {
    ticketDurationDays: ticketDetails.ticketDurationDays,
    checkInWindow: ticketDetails.checkInWindow
      ? {
          minutesBefore: ticketDetails.checkInWindow.minutesBefore,
          minutesAfter: ticketDetails.checkInWindow.minutesAfter,
          blockScan: ticketDetails.checkInWindow.blockScan,
          autoRebook: ticketDetails.checkInWindow.autoRebook,
        }
      : undefined,
    postStartPurchase: ticketDetails.postStartPurchase
      ? {
          allowLateTicketPurchase: ticketDetails.postStartPurchase.allowLateTicketPurchase,
          minutesAfter: ticketDetails.postStartPurchase.minutesAfter,
        }
      : undefined,
    purchaseAvailability: ticketDetails.purchaseAvailability,
    ticketPrintLines: ticketDetails.ticketPrintLines,
    hideEventDate: ticketDetails.hideEventDate,
    doNotPrint: ticketDetails.doNotPrint,
    expireFromEventDate: ticketDetails.expireFromEventDate,
  };
}

/**
 * Determines if the event has order forms
 *
 * @param event - The event to check
 * @returns true if the event has order forms
 */
export function hasOrderForms(event: EventVo): boolean {
  return Array.isArray(event.orderForms) && event.orderForms.length > 0;
}

/**
 * Determines if the event has resources
 *
 * @param event - The event to check
 * @returns true if the event has resources
 */
export function hasResources(event: EventVo): boolean {
  return (
    (Array.isArray(event.resources) && event.resources.length > 0) ||
    (Array.isArray(event.resourceDetails) && event.resourceDetails.length > 0) ||
    (Array.isArray(event.resourceIds) && event.resourceIds.length > 0)
  );
}

/**
 * Transforms an EventVo (Event Instance) to WillCallOrderItemsByEventVo
 *
 * @param event - The event instance from the API
 * @returns WillCallOrderItemsByEventVo with transformed event data and initial items
 *
 * @example
 * ```typescript
 * const willCallEvent = transformEventToWillCallOrderItemsByEvent(eventVo);
 * // Returns event in WillCallOrderItemsByEventVo format with first 5 price list items
 * ```
 */
/**
 * Minimal shape of a single reseller price entry as returned by the reseller event API.
 * Each entry corresponds to a person type and carries optional discount metadata.
 */
export interface ResellerPriceEntry {
  personType?: { id?: string };
  discountId?: string;
  discountName?: string;
  discountedPrice?: string;
  discountedBasePrice?: string;
}

/**
 * Applies reseller discount metadata (discountId / discountName) to:
 *  1. The order items inside `eventItem.items[]`, matched by `personType.id === item.itemTypeId`.
 *     A discount is only applied when the item has a real price reduction
 *     (`unitPrice !== discountedUnitPrice`) and does not already carry a `discountId`.
 *  2. The cached event price list entries in `ctrlEvents[eventId].priceList.prices[]`,
 *     matched by `personType.id`. Existing `discountId` values are never overwritten.
 *     When the discount is applied, the reseller `discountedPrice` / `discountedBasePrice`
 *     are copied onto the entry so tickets added from the dropdown reflect the discount.
 *
 * @param eventItem    - The order event item group (contains items[] and eventId)
 * @param resellerPrices - Price entries from the reseller API response
 * @param ctrlEvents   - The controller's local event cache keyed by eventId
 */
export function applyResellerDiscounts(
  eventItem: WillCallOrderItemsByEventVo,
  resellerPrices: ResellerPriceEntry[],
  ctrlEvents: Record<string, EventVo>,
): void {
  if (!resellerPrices.length) {
    return;
  }

  // Build a lookup map: personType.id -> reseller discount metadata + discounted prices
  const discountByPersonTypeId = new Map<
    string,
    { discountId: string; discountName: string; discountedPrice?: string; discountedBasePrice?: string }
  >();
  for (const price of resellerPrices) {
    if (price.personType?.id && price.discountId) {
      discountByPersonTypeId.set(price.personType.id, {
        discountId: price.discountId,
        discountName: price.discountName ?? '',
        discountedPrice: price.discountedPrice,
        discountedBasePrice: price.discountedBasePrice,
      });
    }
  }

  if (discountByPersonTypeId.size === 0) {
    return;
  }

  // Apply to order items — only when a real price reduction exists
  for (const item of eventItem.items) {
    const discount = item.itemTypeId ? discountByPersonTypeId.get(item.itemTypeId) : undefined;
    if (discount && !item.discountId && item.unitPrice !== item.discountedUnitPrice) {
      item.discountId = discount.discountId;
      item.discountName = discount.discountName;
    }
  }

  // Apply to the cached event price list
  const event = eventItem.eventId ? ctrlEvents[eventItem.eventId] : undefined;
  if (event?.priceList?.prices) {
    for (const price of event.priceList.prices) {
      const discount = price.personType?.id ? discountByPersonTypeId.get(price.personType.id) : undefined;
      if (discount && !price.discountId) {
        price.discountId = discount.discountId;
        price.discountName = discount.discountName;
        // Propagate the reseller discounted prices too: the base event detail carries no
        // discount fields, so without this a ticket added from the dropdown would fall back
        // to the full/base price instead of reflecting the reseller discount.
        if (discount.discountedPrice !== undefined) {
          price.discountedPrice = discount.discountedPrice;
        }
        if (discount.discountedBasePrice !== undefined) {
          price.discountedBasePrice = discount.discountedBasePrice;
        }
      }
    }
  }
}

/**
 * The subset of price fields an order item may carry. Base prices represent the
 * tax-excluded amounts; the unit prices are the regular (tax-inclusive) amounts.
 * The `taxInclusive*` fields preserve the original tax-inclusive prices after the
 * display fields have been normalized to base, so order totals can still compute tax.
 */
export interface BasePriceDisplayItem {
  unitPrice?: string | number | null;
  discountedUnitPrice?: string | number | null;
  basePrice?: string | number | null;
  discountedBasePrice?: string | number | null;
  taxInclusiveUnitPrice?: string | number | null;
  taxInclusiveDiscountedUnitPrice?: string | number | null;
}

/**
 * Normalizes an order item's displayed prices to base (tax-excluded) pricing
 * when sales tax is enabled and a base price is available.
 *
 * This mirrors the display logic in the checkout and print views
 * (`OrdersCheckoutController.showsBasePricing` / `OrdersPrintController.getUnitPrice`):
 * when `enableSalesTax` is on and the item has a `basePrice`, the displayed
 * `unitPrice` / `discountedUnitPrice` are replaced with the base equivalents so the
 * edit page shows prices without tax, consistent with the rest of the order flow.
 *
 * Before overwriting, the original tax-inclusive prices are preserved on
 * `taxInclusiveUnitPrice` / `taxInclusiveDiscountedUnitPrice` so the order total
 * section can still reconstruct the tax and the true (tax-inclusive) total. The
 * capture is skipped when the field already holds the base value, so repeated calls
 * (re-processing, coupon re-application) don't clobber the preserved originals — but
 * a freshly-applied coupon price (which differs from base) is re-captured.
 *
 * The item is mutated in place (and also returned for convenience). Only the
 * display-facing price fields are touched — these are never sent to the backend,
 * which recomputes prices from quantity / discountId on save.
 *
 * @param item - The order item to normalize
 * @param enableSalesTax - Whether the tenant has sales tax enabled
 * @returns The same item, mutated
 *
 * @example
 * ```typescript
 * applyBasePriceDisplay({ unitPrice: '10.80', basePrice: '10.00' }, true);
 * // { unitPrice: '10.00', discountedUnitPrice: '10.00', basePrice: '10.00', taxInclusiveUnitPrice: '10.80' }
 * ```
 */
export function applyBasePriceDisplay<T extends BasePriceDisplayItem>(item: T, enableSalesTax: boolean): T {
  if (enableSalesTax && item && item.basePrice != null) {
    const discountedBase = item.discountedBasePrice ?? item.basePrice;

    // Preserve the tax-inclusive prices (only when the field still holds a
    // non-base value) so they survive the overwrite below.
    if (item.unitPrice != null && item.unitPrice !== item.basePrice) {
      item.taxInclusiveUnitPrice = item.unitPrice;
    }
    if (item.discountedUnitPrice != null && item.discountedUnitPrice !== discountedBase) {
      item.taxInclusiveDiscountedUnitPrice = item.discountedUnitPrice;
    }

    item.unitPrice = item.basePrice;
    item.discountedUnitPrice = discountedBase;
  }
  return item;
}

export function transformEventToWillCallOrderItemsByEvent(
  event: EventVo,
  enableSalesTax = false,
): WillCallOrderItemsByEventVo {
  return {
    eventId: event.id,
    eventName: event.name,
    eventDate: event.startTime,
    eventTemplateId: event.templateId,
    state: event.state,
    cancellationDate: event.cancellationDate,
    admissionType: event.admissionType as AdmissionType,
    hasForms: hasOrderForms(event),
    hasResources: hasResources(event),
    ticketDetails: transformTicketDetails(event.ticketDetails),
    orderForms: event.orderForms?.map((form) => ({
      ...form,
      uuid: uuidv4(), // Assign a unique identifier for tracking form responses in the UI
    })),
    items: ensureArray(event.priceList?.prices)
      .slice(0, 5)
      .map((item) => {
        const itemTypeName = item.personType?.name ?? '';

        return applyBasePriceDisplay(
          {
            discountedUnitPrice: item.discountedPrice || item.price,
            discountId: item.discountId,
            itemType: 'Event',
            itemTypeName,
            quantity: 0,
            resolvedQuantity: 0,
            ticketTypeId: item.personType?.id,
            ticketTypeName: itemTypeName,
            ticketTypeDescription: item.personType?.description ?? '',
            unitPrice: item.price,
            basePrice: item.basePrice,
            discountedBasePrice: item.discountedBasePrice ?? item.basePrice,
            salesChannels: item.salesChannels,
            ticketAddedToOrder: true,
          },
          enableSalesTax,
        );
      }),
  };
}

export function transformOrderFormsUpdateItem(order: Pick<OrderEditModel, 'orderId' | 'customerId'>, form: OrderFormWithResponse): OrderFormsUpdateItem {
  return {
    customerId: order.customerId ?? '',
    itemType: 'event',
    itemId: form.event.id,
    uuid: form.uuid,
    formId: form.id,
    responseForms: form.response ? [form.response as OrderFormsUpdateResponseForm] : [],
  };
}

/**
 * Builds form items for order update, filtering out original forms without responses
 * to avoid duplicates while including new forms regardless of response status.
 */
export function buildFormItemsForOrder(
  forms: OrderFormWithResponse[],
  originalOrderFormUuids: Set<string>,
  order: Pick<OrderEditModel, 'orderId' | 'customerId'>,
): OrderFormsUpdateItem[] {
  return forms
    .filter((form) => {
      const isOriginalForm = originalOrderFormUuids.has(form.uuid);
      const hasResponse = form.response !== null && form.response !== undefined;

      // Exclude original forms without response (to avoid duplicates)
      // Include new forms (regardless of response)
      // Include forms with response (regardless of whether they're original)
      return !isOriginalForm || hasResponse;
    })
    .map((form) => transformOrderFormsUpdateItem(order, form));
}
